import React from 'react';
import { MapPin, Award, Mail, Phone, ExternalLink, Star, Clock } from 'lucide-react';
import type { CandidateMatch } from '../shared/types';

interface SimpleSearchResultsProps {
  matches: CandidateMatch[];
  searchTime: number;
  isLoading: boolean;
  query: string;
}

export function SimpleSearchResults({ matches, searchTime, isLoading, query }: SimpleSearchResultsProps) {
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 pt-2 pb-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Searching through students...</p>
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 pt-2 pb-8">
        <div className="text-center">
          <div className="bg-gray-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Star className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl text-white mb-2">No students found</h3>
          <p className="text-gray-400">Try adjusting your search terms or using different keywords.</p>
        </div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-400';
    if (score >= 0.6) return 'text-yellow-400';
    if (score >= 0.4) return 'text-orange-400';
    return 'text-red-400';
  };

  const getFitBadgeColor = (fit: string) => {
    switch (fit) {
      case 'excellent': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'good': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'fair': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 pt-2 pb-8">
      {/* Results Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-light text-white mb-1">
            Found {matches.length} student{matches.length !== 1 ? 's' : ''}
          </h2>
          <p className="text-gray-400">for "{query}"</p>
        </div>
        <div className="flex items-center space-x-1 text-sm text-gray-400">
          <Clock className="w-4 h-4" />
          <span>{searchTime}ms</span>
        </div>
      </div>

      {/* Results Grid */}
      <div className="space-y-6">
        {matches.map((match, index) => (
          <div 
            key={match.candidate.id} 
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-xl font-medium text-white">{match.candidate.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getFitBadgeColor(match.matchDetails.overallFit)}`}>
                    {match.matchDetails.overallFit} match
                  </span>
                </div>
                <p className="text-blue-400 font-medium mb-1">{match.candidate.title}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-3">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{match.candidate.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Award className="w-4 h-4" />
                    <span>{match.candidate.yearsOfExperience} years</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className={`text-3xl font-bold ${getScoreColor(match.score)}`}>
                  {Math.round(match.score * 100)}%
                </div>
                <p className="text-xs text-gray-400 mt-1">Match</p>
              </div>
            </div>

            {/* Summary */}
            <p className="text-gray-300 mb-4 line-clamp-2">{match.candidate.summary}</p>

            {/* Skills */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {match.candidate.skills.slice(0, 6).map((skill, skillIndex) => {
                  const skillMatch = match.matchDetails.skillsMatch.find(sm => 
                    sm.skill.toLowerCase().includes(skill.toLowerCase()) || 
                    skill.toLowerCase().includes(sm.skill.toLowerCase())
                  );
                  
                  return (
                    <span
                      key={skillIndex}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        skillMatch 
                          ? skillMatch.match === 'exact' 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      {skill}
                    </span>
                  );
                })}
                {match.candidate.skills.length > 6 && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
                    +{match.candidate.skills.length - 6} more
                  </span>
                )}
              </div>
            </div>

            {/* Recent Experience */}
            {match.candidate.experience.length > 0 && (
              <div className="mb-4 p-4 bg-gray-900/50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Latest Role</h4>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{match.candidate.experience[0].position}</p>
                    <p className="text-gray-400 text-sm">{match.candidate.experience[0].company}</p>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {match.candidate.experience[0].startDate} - {match.candidate.experience[0].endDate || 'Present'}
                  </p>
                </div>
              </div>
            )}

            {/* Contact Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span>{match.candidate.email}</span>
                </div>
                {match.candidate.phone && (
                  <div className="flex items-center space-x-1 text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span>{match.candidate.phone}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                {match.candidate.linkedinUrl && (
                  <a
                    href={match.candidate.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </a>
                )}
                <button 
                  onClick={() => window.open('https://example.resumai.chat', '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Contact
                </button>
              </div>
            </div>

            {/* Match Explanation */}
            <div className="mt-4 p-3 bg-gray-900/30 rounded-lg">
              <p className="text-gray-300 text-sm">{match.explanation}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
